/**
 * タスク編集 Sheet
 *
 * 案1+案2ハイブリッド:
 * - 常に表示: タイトル、ステータスチップ、属性チップバー、サブタスク、保存
 * - チップバー: エネルギー、優先度、見積、締切、タグ、先行タスク (タップでポップオーバー編集)
 * - 「詳細」折りたたみ: 説明、スケジュール、完了日時
 */

import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Editor } from '@tiptap/react'
import {
  ArrowRight,
  Battery,
  Calendar,
  Circle,
  CircleCheck,
  Clock,
  Copy,
  Flag,
  Folder,
  GripVertical,
  Lock,
  Maximize2,
  Minimize2,
  Play,
  Plus,
  Sparkles,
  Tag,
  Trash2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { updateTodo as apiUpdateTodo } from '@/api/services/todo'
import type { TodoEnergy, TodoResponse, TodoStatus, TodoUpdate } from '@/api/types/todo'
import { NotionEditor } from '@/components/shared/notion-editor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { parseUtcDate } from '@/lib/date-utils'
import { useTodoStore } from '@/store/useTodoStore'

import { compareTodoPosition } from './sort'
import { buildTodoImplementCommand, copyTodoImplementCommand } from './todo-command'
import { chipClass, chipClassBlocked, energyLabel, statusLabel } from './todo-edit-labels'
import { TodoBookLink } from './TodoBookLink'
import { appendTodoMemoTemplate } from './todoMemoTemplate'

type Props = {
  todo: TodoResponse | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const TodoEditSheet = ({ todo, open, onOpenChange }: Props) => {
  const patchTodo = useTodoStore((s) => s.patchTodo)
  const addTodo = useTodoStore((s) => s.addTodo)
  const removeTodo = useTodoStore((s) => s.removeTodo)
  const todos = useTodoStore((s) => s.todos)
  const [title, setTitle] = useState('')
  const [deadline, setDeadline] = useState('')
  const [estimatedMinutes, setEstimatedMinutes] = useState('')
  const [priority, setPriority] = useState('50')
  const [energy, setEnergy] = useState<TodoEnergy>('medium')
  const [status, setStatus] = useState<TodoStatus>('pending')
  const [tags, setTags] = useState('')
  const [project, setProject] = useState('')
  const [scheduledStart, setScheduledStart] = useState('')
  const [scheduledEnd, setScheduledEnd] = useState('')
  const [completedAt, setCompletedAt] = useState('')
  const [blockedById, setBlockedById] = useState('')
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const { isSubmitting: isAddingChild, run: runAddChild } = useSubmitLock()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const isInitializing = useRef(true)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>(null)
  const performSaveRef = useRef<(() => Promise<void>) | null>(null)
  // 閉じた直後のフラッシュ保存用に、直前まで編集していた Todo を保持する
  const lastTodoRef = useRef<TodoResponse | null>(null)
  const [memoEditor, setMemoEditor] = useState<Editor | null>(null)

  const handleInsertTemplate = useCallback(() => {
    if (!memoEditor || memoEditor.isDestroyed) return
    appendTodoMemoTemplate(memoEditor)
  }, [memoEditor])

  // メモ本文の空/非空を追跡し、ストアの has_memo を楽観的に同期する。
  // 保存 'saved' 通知ではなくエディタの 'update' を購読することで、
  // 入力/削除に即座に反応し、リストのアイコンをリアルタイム更新する。
  const todoId = todo?.id
  useEffect(() => {
    if (!memoEditor || todoId == null) return
    const sync = () => {
      if (memoEditor.isDestroyed) return
      const nextHasMemo = memoEditor.getText().trim().length > 0
      useTodoStore.setState((state) => {
        const target = state.todos.find((t) => t.id === todoId)
        if (!target) return state
        if ((target.has_memo ?? false) === nextHasMemo) return state
        return {
          todos: state.todos.map((t) => (t.id === todoId ? { ...t, has_memo: nextHasMemo } : t)),
        }
      })
    }
    sync()
    memoEditor.on('update', sync)
    return () => {
      memoEditor.off('update', sync)
    }
  }, [memoEditor, todoId])

  useEffect(() => {
    setMemoEditor(null)
  }, [todo?.id])

  useEffect(() => {
    if (!todo) return
    lastTodoRef.current = todo
    isInitializing.current = true
    setTitle(todo.title)
    setDeadline(todo.deadline ? toLocalDatetime(todo.deadline) : '')
    setEstimatedMinutes(todo.estimated_minutes?.toString() ?? '')
    setPriority(todo.priority.toString())
    setEnergy(todo.energy)
    setStatus(todo.status)
    setTags(todo.tags.join(', '))
    setProject(todo.project ?? '')
    setScheduledStart(todo.scheduled_start ? toLocalDatetime(todo.scheduled_start) : '')
    setScheduledEnd(todo.scheduled_end ? toLocalDatetime(todo.scheduled_end) : '')
    setCompletedAt(todo.completed_at ? toLocalDatetime(todo.completed_at) : '')
    setBlockedById(todo.blocked_by_id?.toString() ?? '')
    setSaveStatus('idle')
    // 次のレンダーサイクルで初期化フラグを解除
    requestAnimationFrame(() => {
      isInitializing.current = false
    })
  }, [todo])

  // デバウンス自動保存
  useEffect(() => {
    if (!todo || isInitializing.current) return
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(() => {
      autoSaveTimer.current = null
      void performSaveRef.current?.()
    }, 800)
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    deadline,
    estimatedMinutes,
    priority,
    energy,
    status,
    tags,
    project,
    scheduledStart,
    scheduledEnd,
    completedAt,
    blockedById,
  ])

  // ストアから最新の子Todoを取得
  const children = todos.filter((t) => t.parent_id === todo?.id).sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = children.findIndex((c) => c.id === active.id)
      const newIndex = children.findIndex((c) => c.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = [...children]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      // 楽観的に store を一括更新
      const positionMap = new Map<number, number>()
      for (let i = 0; i < reordered.length; i++) {
        positionMap.set(reordered[i].id, i)
      }
      useTodoStore.setState((state) => ({
        todos: state.todos.map((t) => {
          const newPos = positionMap.get(t.id)
          return newPos != null ? { ...t, position: newPos } : t
        }),
      }))

      // バックグラウンドで API 同期
      for (const [id, newPos] of positionMap) {
        const original = children.find((c) => c.id === id)
        if ((original?.position ?? 0) !== newPos) {
          void apiUpdateTodo(id, { position: newPos })
        }
      }
    },
    [children],
  )

  const handleAddChild = async () => {
    if (!todo || !newSubtaskTitle.trim()) return
    const maxPosition = children.length > 0 ? Math.max(...children.map((c) => c.position ?? 0)) : -1
    await runAddChild(async () => {
      try {
        await addTodo({
          title: newSubtaskTitle.trim(),
          parent_id: todo.id,
          status: 'pending',
          source: 'manual',
          position: maxPosition + 1,
        })
        setNewSubtaskTitle('')
      } catch {
        toast.error('サブタスク追加に失敗しました')
      }
    })
  }

  const handleToggleChild = async (child: TodoResponse) => {
    try {
      await patchTodo(child.id, { status: child.status === 'done' ? 'pending' : 'done' })
    } catch {
      toast.error('サブタスク更新に失敗しました')
    }
  }

  const handleDeleteChild = async (childId: number) => {
    try {
      await removeTodo(childId)
    } catch {
      toast.error('サブタスク削除に失敗しました')
    }
  }

  const performSave = async () => {
    // 閉じた直後のフラッシュでは todo が null になっているため、直前の編集対象へフォールバックする
    const target = todo ?? lastTodoRef.current
    if (!target) return
    if (!title.trim()) return
    const hasStart = scheduledStart.trim() !== ''
    const hasEnd = scheduledEnd.trim() !== ''
    if (hasStart !== hasEnd) return
    if (hasStart && hasEnd && new Date(scheduledStart) >= new Date(scheduledEnd)) return
    const computedMinutes =
      hasStart && hasEnd
        ? Math.round((new Date(scheduledEnd).getTime() - new Date(scheduledStart).getTime()) / 60000)
        : estimatedMinutes
          ? parseInt(estimatedMinutes, 10)
          : null
    setSaveStatus('saving')
    try {
      const payload: TodoUpdate = {
        title: title.trim() || target.title,
        deadline: deadline ? new Date(deadline).toISOString() : null,
        estimated_minutes: computedMinutes,
        priority: parseInt(priority, 10) || 50,
        energy,
        status,
        scheduled_start: scheduledStart ? new Date(scheduledStart).toISOString() : null,
        scheduled_end: scheduledEnd ? new Date(scheduledEnd).toISOString() : null,
        completed_at: completedAt ? new Date(completedAt).toISOString() : null,
        blocked_by_id: blockedById ? parseInt(blockedById, 10) : null,
        project: project.trim() || null,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      }
      await patchTodo(target.id, payload)
      setSaveStatus('saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '更新に失敗しました')
      setSaveStatus('idle')
    }
  }
  useEffect(() => {
    performSaveRef.current = performSave
  })

  // 編集直後に閉じるとデバウンス待ちの変更が破棄されてしまうため、閉じる時点で書き出す
  useEffect(() => {
    if (open) return
    if (!autoSaveTimer.current) return
    clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = null
    void performSaveRef.current?.()
  }, [open])

  const blockerTitle = blockedById ? todos.find((t) => t.id === parseInt(blockedById, 10))?.title : null
  const projectOptions = useMemo(() => {
    const set = new Set<string>()
    for (const t of todos) {
      if (t.project) set.add(t.project)
    }
    return [...set].sort()
  }, [todos])

  // プロジェクトは一覧のグループ分けに直結するため、保存完了を待たずストアへ先に反映する
  const applyProject = useCallback(
    (next: string) => {
      setProject(next)
      if (todoId == null) return
      const value = next.trim() || null
      useTodoStore.setState((state) => {
        const current = state.todos.find((t) => t.id === todoId)
        if (!current || (current.project ?? null) === value) return state
        return { todos: state.todos.map((t) => (t.id === todoId ? { ...t, project: value } : t)) }
      })
    },
    [todoId],
  )
  const deadlineDisplay = deadline
    ? new Date(deadline).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
    : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`w-full overflow-hidden gap-0 ${isFullscreen ? 'sm:max-w-none' : 'sm:max-w-md'}`}
      >
        <SheetHeader className="pb-0">
          <div className="flex min-h-8 items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle>{todo?.is_event ? '📅 予定編集' : 'タスク編集'}</SheetTitle>
                {todo && (
                  <Badge
                    variant="outline"
                    asChild
                    className="cursor-pointer font-mono text-[10px] text-muted-foreground hover:bg-accent"
                  >
                    <button
                      type="button"
                      aria-label={`「${buildTodoImplementCommand(todo.id)}」をコピー`}
                      onClick={() => void copyTodoImplementCommand(todo.id)}
                    >
                      <Copy className="size-3" />#{todo.id}
                    </button>
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 me-7">
              <button
                type="button"
                aria-label={isFullscreen ? '通常表示に戻す' : '全画面表示にする'}
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0! hidden sm:inline-flex"
                onClick={() => setIsFullscreen((v) => !v)}
              >
                {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
              </button>
              <button
                type="button"
                aria-label="タスクを削除"
                className="shrink-0 text-muted-foreground hover:text-destructive transition-colors p-0!"
                onClick={async () => {
                  if (!todo) return
                  await removeTodo(todo.id)
                  toast.success('タスクを削除しました')
                  onOpenChange(false)
                }}
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex flex-1 min-h-0 flex-col gap-3 mt-4 px-1 overflow-y-auto">
          {/* ===== タイトル + 完了トグル ===== */}
          <div className="flex items-center gap-2 ms-1">
            {!todo?.is_event && (
              <button
                type="button"
                aria-label={todo?.status === 'done' ? '未完了に戻す' : '完了にする'}
                className="shrink-0 text-muted-foreground hover:text-emerald-500 transition-colors p-0!"
                onClick={async () => {
                  if (!todo) return
                  const next = todo.status === 'done' ? 'pending' : 'done'
                  try {
                    await patchTodo(todo.id, { status: next })
                    toast.success(next === 'done' ? 'タスクを完了しました' : '未完了に戻しました')
                    onOpenChange(false)
                  } catch {
                    toast.error('ステータス更新に失敗しました')
                  }
                }}
              >
                {todo?.status === 'done' ? (
                  <CircleCheck className="size-5 text-emerald-500" />
                ) : (
                  <Circle className="size-5" />
                )}
              </button>
            )}
            {!todo?.is_event && todo?.status !== 'done' && (
              <button
                type="button"
                aria-label="タスクを開始する"
                className="shrink-0 inline-flex size-7 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors p-0!"
                onClick={async () => {
                  if (!todo) return
                  try {
                    await patchTodo(todo.id, {
                      scheduled_start: new Date().toISOString(),
                      status: 'in_progress',
                    })
                    toast.success('タスクを開始しました')
                    onOpenChange(false)
                  } catch {
                    toast.error('開始に失敗しました')
                  }
                }}
              >
                <Play className="size-4 fill-current" />
              </button>
            )}
            <Textarea
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="タスク名"
              className="text-base font-medium resize-none min-h-9 field-sizing-content"
              rows={1}
            />
          </div>

          {todo?.source === 'library_book' && todo.source_ref && <TodoBookLink isbn={todo.source_ref} />}

          {/* ===== ステータス (横並びチップ) ===== */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {(['pending', 'in_progress', 'scheduled', 'done', 'dismissed'] as TodoStatus[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                  status === s
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted-foreground/20 text-muted-foreground hover:border-primary/50'
                }`}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>

          {/* ===== Notion風メタデータ行 ===== */}
          <div className="flex flex-col">
            <MetaDateRow
              label="開始予定"
              icon={<Play className="size-3" />}
              value={scheduledStart}
              onChange={setScheduledStart}
            />
            <MetaDateRow
              label="終了予定"
              icon={<Clock className="size-3" />}
              value={scheduledEnd}
              onChange={setScheduledEnd}
            />
            {status === 'done' && (
              <MetaDateRow
                label="完了日時"
                icon={<CircleCheck className="size-3 text-emerald-500" />}
                value={completedAt}
                onChange={setCompletedAt}
              />
            )}
          </div>

          {/* ===== 属性チップバー ===== */}
          {!todo?.is_event && (
            <div className="flex flex-wrap gap-1.5">
              {/* プロジェクト */}
              <Popover
                onOpenChange={(popoverOpen) => {
                  if (!popoverOpen) applyProject(project)
                }}
              >
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(!!project.trim())}>
                    <Folder className="size-3" />
                    {project.trim() || '+プロジェクト'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3">
                  <label className="text-xs text-muted-foreground">プロジェクト</label>
                  <Input
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder="未設定"
                    className="mt-1 h-8"
                  />
                  {projectOptions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {projectOptions.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => applyProject(project === p ? '' : p)}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                            project === p
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-muted-foreground/20 text-muted-foreground hover:border-primary/50'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              {/* エネルギー */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(true)}>
                    <Battery className="size-3" />
                    {energyLabel(energy)}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-2">
                  <div className="flex flex-col gap-1">
                    {(['low', 'medium', 'high'] as TodoEnergy[]).map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => setEnergy(e)}
                        className={`text-xs px-2 py-1 rounded text-left transition-colors ${energy === e ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                      >
                        {energyLabel(e)}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* 優先度 */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(true)}>
                    <Flag className="size-3" />
                    P:{priority}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-3">
                  <label className="text-xs text-muted-foreground">優先度 (0-100)</label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="mt-1 h-8"
                  />
                </PopoverContent>
              </Popover>

              {/* 見積 */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(!!estimatedMinutes)}>
                    <Clock className="size-3" />
                    {estimatedMinutes ? `${estimatedMinutes}m` : '+見積'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-3">
                  <label className="text-xs text-muted-foreground">見積もり（分）</label>
                  <Input
                    type="number"
                    min={0}
                    value={estimatedMinutes}
                    onChange={(e) => setEstimatedMinutes(e.target.value)}
                    placeholder="30"
                    className="mt-1 h-8"
                  />
                </PopoverContent>
              </Popover>

              {/* 締切 */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(!!deadline)}>
                    <Calendar className="size-3" />
                    {deadlineDisplay ?? '+締切'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3">
                  <label className="text-xs text-muted-foreground">締切</label>
                  <Input
                    type="datetime-local"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="mt-1 h-8"
                  />
                  {deadline && (
                    <Button variant="ghost" size="sm" className="mt-1 h-6 text-xs" onClick={() => setDeadline('')}>
                      クリア
                    </Button>
                  )}
                </PopoverContent>
              </Popover>

              {/* タグ */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={chipClass(!!tags.trim())}>
                    <Tag className="size-3" />
                    {tags.trim()
                      ? tags
                          .split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .slice(0, 2)
                          .join(', ') + (tags.split(',').filter((t) => t.trim()).length > 2 ? '…' : '')
                      : '+タグ'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-3">
                  <label className="text-xs text-muted-foreground">タグ（カンマ区切り）</label>
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="work, urgent"
                    className="mt-1 h-8"
                  />
                </PopoverContent>
              </Popover>

              {/* 先行タスク */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" className={blockedById ? chipClassBlocked() : chipClass(false)}>
                    <Lock className="size-3" />
                    {blockerTitle ? blockerTitle.slice(0, 10) + (blockerTitle.length > 10 ? '…' : '') : '+先行'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3">
                  <label className="text-xs text-muted-foreground">先行タスク</label>

                  {todo?.parent_id != null &&
                    (() => {
                      const siblings = todos
                        .filter((t) => t.parent_id === todo.parent_id && t.id !== todo.id)
                        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                      const currentPos = todo.position ?? 0
                      const candidates = siblings.filter((s) => (s.position ?? 0) < currentPos)
                      const prevSibling = candidates[candidates.length - 1]
                      if (!prevSibling) return null
                      const isSet = blockedById === prevSibling.id.toString()
                      return (
                        <button
                          type="button"
                          className={`mt-2 w-full flex items-center gap-2 rounded border px-2 py-1.5 text-xs transition-colors ${
                            isSet
                              ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                              : 'border-muted-foreground/20 text-muted-foreground hover:border-orange-500/50'
                          }`}
                          onClick={() => setBlockedById(isSet ? '' : prevSibling.id.toString())}
                        >
                          <Lock className="size-3 shrink-0" />
                          <span className="truncate">前のタスク: {prevSibling.title}</span>
                          {isSet && <span className="ml-auto text-[10px]">✓</span>}
                        </button>
                      )
                    })()}

                  <Select value={blockedById || '_none'} onValueChange={(v) => setBlockedById(v === '_none' ? '' : v)}>
                    <SelectTrigger className="mt-2 h-8 text-xs">
                      <SelectValue placeholder="なし" />
                    </SelectTrigger>
                    <SelectContent
                      position="popper"
                      side="bottom"
                      sideOffset={4}
                      className="max-h-(--radix-select-content-available-height)"
                    >
                      <SelectItem value="_none">なし</SelectItem>
                      {todos
                        .filter(
                          (t) => t.id !== todo?.id && t.status !== 'done' && t.status !== 'dismissed' && !t.is_event,
                        )
                        .sort(compareTodoPosition)
                        .map((t) => (
                          <SelectItem key={t.id} value={t.id.toString()}>
                            #{t.id} {t.title}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* ===== サブタスク ===== */}
          {!todo?.is_event && (
            <div className="flex flex-col gap-2 p-3 border-b border-border/50">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-muted-foreground">
                  サブタスク ({children.filter((c) => c.status === 'done').length}/{children.length})
                </label>
              </div>

              {children.length > 0 &&
                (() => {
                  const deps = children.filter(
                    (c) => c.blocked_by_id != null && children.some((s) => s.id === c.blocked_by_id),
                  )
                  return deps.length > 0 ? (
                    <div className="flex flex-col gap-1 rounded border border-orange-500/20 bg-orange-500/5 p-2">
                      <span className="text-[10px] font-semibold text-orange-500 flex items-center gap-1">
                        <Lock className="size-3" />
                        依存関係
                      </span>
                      {deps.map((dep) => {
                        const blocker = children.find((c) => c.id === dep.blocked_by_id)
                        return (
                          <div key={dep.id} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span
                              className={blocker?.status === 'done' ? 'line-through' : 'font-medium text-foreground'}
                            >
                              {blocker?.title ?? '?'}
                            </span>
                            <ArrowRight className="size-3 text-orange-500 shrink-0" />
                            <span>{dep.title}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : null
                })()}

              {children.length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={children.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                    <ul className="flex flex-col gap-1">
                      {children.map((child) => (
                        <SortableChildRow
                          key={child.id}
                          child={child}
                          onToggle={handleToggleChild}
                          onDelete={handleDeleteChild}
                          onRename={(id, newTitle) =>
                            void patchTodo(id, { title: newTitle }).catch(() =>
                              toast.error('タイトル更新に失敗しました'),
                            )
                          }
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </DndContext>
              )}

              <div className="flex items-center gap-1.5 mt-1">
                <Input
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      handleAddChild()
                    }
                  }}
                  placeholder="⌘Enter で追加"
                  className="h-7 text-sm"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={handleAddChild}
                  disabled={isAddingChild || !newSubtaskTitle.trim()}
                  aria-label="サブタスクを追加"
                >
                  <Plus className="size-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== メモ (Notion風エディタ) ===== */}
          {todo && (
            <div className="flex flex-1 min-h-40 flex-col gap-1">
              <div className="flex justify-end px-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleInsertTemplate}
                  className="h-7 gap-1 text-xs text-muted-foreground"
                  aria-label="エージェント用テンプレートをメモ末尾に挿入"
                >
                  <Sparkles className="size-3.5" />
                  テンプレ挿入
                </Button>
              </div>
              <NotionEditor
                key={todo.id}
                entityType="todo"
                entityId={todo.id}
                placeholder="メモを入力…"
                className="notion-editor-compact flex-1 min-h-40 overflow-y-auto  px-3 py-2 text-sm"
                onEditorReady={(editor) => {
                  setMemoEditor(editor)
                }}
              />
            </div>
          )}

          {/* ===== ステータス表示 ===== */}
          <span className="text-xs text-muted-foreground">
            {saveStatus === 'saving' && '保存中...'}
            {saveStatus === 'saved' && '✓ 保存済み'}
          </span>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ─── Helpers ────────────────────────────────────────────────────

const MetaDateRow = (props: { label: string; icon: ReactNode; value: string; onChange: (v: string) => void }) => {
  const { label, icon, value, onChange } = props
  const [editing, setEditing] = useState(false)
  const display = value
    ? new Date(value).toLocaleString('ja-JP', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '未設定'

  return (
    <div className="flex items-center gap-2 py-1.5 min-h-9">
      <div className="flex items-center gap-1.5 w-20 shrink-0 text-xs text-muted-foreground">
        {icon}
        {label}
      </div>
      {editing ? (
        <Input
          type="datetime-local"
          value={value}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          className="h-7 text-xs flex-1"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`flex-1 text-left text-sm rounded px-1.5 py-0.5 hover:bg-muted transition-colors ${
            value ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          {display}
        </button>
      )}
    </div>
  )
}

// ─── SortableChildRow ───────────────────────────────────────────

const SortableChildRow = (props: {
  child: TodoResponse
  onToggle: (c: TodoResponse) => void
  onDelete: (id: number) => void
  onRename: (id: number, title: string) => void
}) => {
  const { child, onToggle, onDelete, onRename } = props
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: child.id,
    animateLayoutChanges: () => false,
  })
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(child.title)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const commitRename = () => {
    const trimmed = editTitle.trim()
    if (!trimmed || trimmed === child.title) {
      setEditTitle(child.title)
      setEditing(false)
      return
    }
    onRename(child.id, trimmed)
    setEditing(false)
  }

  return (
    <li ref={setNodeRef} style={style} className="flex items-center gap-2 group">
      <button
        type="button"
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-grab touch-none p-0!"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onToggle(child)}
        aria-label={`${child.title}を${child.status === 'done' ? '未完了' : '完了'}にする`}
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-0!"
      >
        {child.status === 'done' ? <CircleCheck className="size-4 text-emerald-500" /> : <Circle className="size-4" />}
      </button>
      {editing ? (
        <Input
          autoFocus
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              commitRename()
            }
            if (e.key === 'Escape') {
              setEditTitle(child.title)
              setEditing(false)
            }
          }}
          className="h-6 text-sm flex-1"
        />
      ) : (
        <span
          className={`flex-1 text-sm cursor-text ${child.status === 'done' ? 'line-through text-muted-foreground' : ''}`}
          onClick={() => {
            setEditTitle(child.title)
            setEditing(true)
          }}
        >
          {child.title}
        </span>
      )}
      {!editing && child.estimated_minutes != null && (
        <span className="text-[10px] text-muted-foreground">{child.estimated_minutes}m</span>
      )}
      <button
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-destructive"
        onClick={() => onDelete(child.id)}
        aria-label={`${child.title}を削除`}
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  )
}

// ─── Utils ──────────────────────────────────────────────────────

function toLocalDatetime(iso: string): string {
  try {
    const d = parseUtcDate(iso)
    const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000)
    return jst.toISOString().slice(0, 16)
  } catch {
    return ''
  }
}
